import React, { useEffect, useMemo, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { createColumnHelper, useReactTable, getCoreRowModel, getSortedRowModel, getPaginationRowModel, flexRender } from '@tanstack/react-table';
import { getGroupTitleById, groupIdToTitle } from '../utils/groupTitles';
import ExportModal from '../components/ExportModal';
import './TopicsIntensityAnalysis.scss';
import './Sidebar.scss';
import { getTopicsIntensityMarkerLabel, isNumericTopicsIntensityMarker, isTopicsIntensityMarkerHidden, 
    topicsIntensityMarkersDictionary, isTopicsIntensityMarkerSystem } from './topicsIntensityMarkersDictionary';
import { useRegionsData } from '../hooks/useRegions';
import Pagination from '../components/Pagination';
import DataWindowSlider from '../components/DataWindowSlider';
import { useDataWindow } from '../hooks/useDataWindow';
import ChartsGroup from '../components/ChartsGroup';
import { getMarkerLabel } from "./markersDictionary";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { useGlobalFilters, useGlobalFiltersSubscription } from '../hooks/useGlobalFilters';
import { topicsIntensityStore } from './topicsIntensityStore'; // ← Импорт стора
import { fillDataGaps, getActualDataLength } from '../utils/dataUtils';

ChartJS.register(CategoryScale, LinearScale, PointElement, LineElement, Title, Tooltip, Legend);

const columnHelper = createColumnHelper();

const toDate = (value) => {
  if (value instanceof Date) {
    return value;
  }
  if (value == null) {
    return null;
  }
  if (typeof value === 'number') {
    const ms = value < 1e12 ? value * 1000 : value;
    const date = new Date(ms);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) {
      return null;
    }
    const numeric = Number(trimmed);
    if (!Number.isNaN(numeric)) {
      const ms = numeric < 1e12 ? numeric * 1000 : numeric;
      const date = new Date(ms);
      return Number.isNaN(date.getTime()) ? null : date;
    }
    const date = new Date(trimmed);
    return Number.isNaN(date.getTime()) ? null : date;
  }
  return null;
};

const formatDateCell = (value) => {
  const date = toDate(value);
  return date ? date.toLocaleString('ru-RU') : 'Нет даты';
};
const MARKERS_KEY = 'selectedMarkers_topics';

const TopicsIntensityAnalysis = observer(() => {
  const filtersStore = useGlobalFilters();
  const { regions, isLoading: isGroupsLoading } = useRegionsData();
  const [globalFilters, setGlobalFilters] = useState(filtersStore.filters);
  const [sidebarOpen, setSidebarOpen] = useState(true);

  // Подписка на фильтры
  useGlobalFiltersSubscription((filters) => {
    setGlobalFilters(filters);
  });

  // Обработка данных с заполнением пропусков
  const processedChartData = useMemo(() => {
    if (!topicsIntensityStore.chartData || topicsIntensityStore.chartData.length === 0) {
      return [];
    }

    const { startDate, endDate } = globalFilters;
    if (!startDate || !endDate) {
      return topicsIntensityStore.chartData;
    }

    return fillDataGaps(
      topicsIntensityStore.chartData, 
      startDate, 
      endDate, 
      topicsIntensityStore.aggregation
    );
  }, [topicsIntensityStore.chartData, globalFilters, topicsIntensityStore.aggregation]);

  const [dataWindow, updateDataWindow] = useDataWindow(100, getActualDataLength(processedChartData));

  // Цвета маркеров
  const markerColors = useMemo(() => {
    const colors = [
      '#1976d2', '#d32f2f', '#388e3c', '#f57c00', '#7b1fa2',
      '#0288d1', '#c2185b', '#fbc02d', '#455a64', '#0097a7',
    ];
    const colorMap = {};
    topicsIntensityStore.selectedMarkers.forEach((marker, index) => {
      colorMap[marker] = colors[index % colors.length];
    });
    return colorMap;
  }, [topicsIntensityStore.selectedMarkers]);

  // Все возможные колонки
  const allColumnsFlat = useMemo(() => {
    return [
      columnHelper.accessor('time_stamp', {
        header: getTopicsIntensityMarkerLabel('time_stamp'),
        cell: info => formatDateCell(info.getValue()),
      }),
      columnHelper.accessor('session_id', { header: getTopicsIntensityMarkerLabel('session_id') }),
      columnHelper.accessor('group_id', { 
        header: getTopicsIntensityMarkerLabel('group_id'),
        cell: info => getGroupTitleById(info.getValue())
      }),
      columnHelper.accessor('post_id', { header: getTopicsIntensityMarkerLabel('post_id') }),
      columnHelper.accessor('author_id', { header: getTopicsIntensityMarkerLabel('author_id') }),
      columnHelper.accessor('date', {
        header: getTopicsIntensityMarkerLabel('date'),
        cell: info => formatDateCell(info.getValue()),
      }),
      columnHelper.accessor('text', { header: getTopicsIntensityMarkerLabel('text') }),
      columnHelper.accessor('text_cleaned', { header: getTopicsIntensityMarkerLabel('text_cleaned') }),
      columnHelper.accessor('dict', { header: getTopicsIntensityMarkerLabel('dict') }),
      columnHelper.accessor('intensity', { 
        header: getTopicsIntensityMarkerLabel('intensity'),
        cell: info => isNumericTopicsIntensityMarker('intensity') && info.getValue() != null && !isNaN(info.getValue()) 
          ? Number(info.getValue()).toFixed(3) 
          : info.getValue()
      }),
    ];
  }, []);

  // Видимые колонки
  const columns = useMemo(() => {
    const ALWAYS_HIDDEN = new Set(['time_stamp', 'session_id']);
    const visibleMarkers = [
      ...topicsIntensityStore.selectedMarkers.filter(k => !ALWAYS_HIDDEN.has(k)),
      ...Object.keys(topicsIntensityMarkersDictionary).filter(
        key => isTopicsIntensityMarkerHidden(key) && 
               !isTopicsIntensityMarkerSystem(key) && 
               !ALWAYS_HIDDEN.has(key) && 
               !topicsIntensityStore.selectedMarkers.includes(key)
      )
    ];
    return visibleMarkers.length > 0 
      ? allColumnsFlat.filter(col => col.accessorKey && visibleMarkers.includes(col.accessorKey)) 
      : [];
  }, [topicsIntensityStore.selectedMarkers, allColumnsFlat]);

  // Таблица
  const table = useReactTable({
    data: topicsIntensityStore.tableData,
    columns,
    pageCount: topicsIntensityStore.pageCount,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    manualPagination: true,
    manualSorting: true,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 10 },
      sorting: [{ id: 'date', desc: false }],
    },
    onPaginationChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(table.getState().pagination) : updater;
      table.setState(old => ({ ...old, pagination: newState }));
      topicsIntensityStore.fetchTableData({
        pageIndex: newState.pageIndex,
        pageSize: newState.pageSize,
        sorting: table.getState().sorting,
        globalFilters,
      });
    },
    onSortingChange: (updater) => {
      const newState = typeof updater === 'function' ? updater(table.getState().sorting) : updater;
      table.setState(old => ({ ...old, sorting: newState }));
      topicsIntensityStore.fetchTableData({
        pageIndex: table.getState().pagination.pageIndex,
        pageSize: table.getState().pagination.pageSize,
        sorting: newState,
        globalFilters,
      });
    },
  });

  // Сохранение маркеров в localStorage
  useEffect(() => {
    localStorage.setItem(MARKERS_KEY, JSON.stringify(topicsIntensityStore.selectedMarkers));
  }, [topicsIntensityStore.selectedMarkers]);

  // Загрузка данных при изменении фильтров или агрегации
  useEffect(() => {
    topicsIntensityStore.fetchChartData(globalFilters);
    topicsIntensityStore.fetchTableData({
      pageIndex: 0,
      pageSize: 10,
      sorting: [{ id: 'date', desc: false }],
      globalFilters,
    });
  }, [globalFilters, topicsIntensityStore.aggregation]);

  const handleAggregationChange = (e) => {
    topicsIntensityStore.setAggregation(e.target.value);
  };

  return (
    <div className="topics-intensity-analysis" style={{ display: 'flex' }}>
      <div className="main-content-area">
        {filtersStore.selectedGroups.length === 0 ? (
          <p className="no-data">Выберите сообщества в верхней панели для отображения данных</p>
        ) : (
          <>
            {/* Верхняя панель действий */}
            <div className="action-section">
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <select
                  value={topicsIntensityStore.aggregation}
                  onChange={handleAggregationChange}
                  style={{
                    fontSize: 16,
                    padding: '6px 12px',
                    borderRadius: 8,
                    border: '1px solid #1976d2',
                    color: '#1976d2',
                    fontWeight: 500,
                  }}
                >
                  <option value="1">По дням</option>
                  <option value="7">По неделям</option>
                  <option value="30">По месяцам</option>
                </select>
              </div>

              {/* Кнопка экспорта */}
              {topicsIntensityStore.tableData.length > 0 && (
                <button 
                  onClick={() => topicsIntensityStore.setShowExportModal(true)}
                  title="Экспортировать данные"
                  disabled={topicsIntensityStore.loadingTable}
                  style={{
                    padding: "10px 14px",
                    background: "#f4f6f8",
                    color:  "#222",
                    border: "1px solid #d1d5db",            
                    borderRadius: 8,              
                    transition: "all 0.2s ease",
                    display: "flex",
                    alignItems: "center",
                    gap: 6,
                    fontSize: 15,
                    fontWeight: 500,
                  }}
                  onMouseEnter={(e) => {
                    if (!topicsIntensityStore.loadingTable) {
                      e.currentTarget.style.background = "#e5e7eb";                
                      e.currentTarget.style.boxShadow = "0 4px 16px #3c3c3c3a";
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (!topicsIntensityStore.loadingTable) {
                      e.currentTarget.style.background = "#f4f6f8";
                      e.currentTarget.style.boxShadow = "0 0px 0px #3c3c3c3a";  
                    }
                  }}
                >
                  Экспорт данных
                </button>
              )}
            </div>
            
            {/* Модальное окно экспорта */}
            <ExportModal
              isOpen={topicsIntensityStore.showExportModal}
              onClose={() => topicsIntensityStore.setShowExportModal(false)}
              dataType="topics_intensity"
              title="Экспорт интенсивности тем"
            />

            {/* График */}
            <div
              style={{
                minWidth: 1000,
                width: "100%",
                margin: '0 auto',
                background: '#fff',
                borderRadius: 12,
                boxShadow: '0 2px 12px #0001',
                padding: 24,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
              }}
            >
              {topicsIntensityStore.loadingChart ? (
                <p className="loading">Загрузка...</p>
              ) : topicsIntensityStore.errorChart ? (
                <p className="error">{topicsIntensityStore.errorChart}</p>
              ) : topicsIntensityStore.chartData.length === 0 ? (
                <p className="no-data">Нет данных для отображения</p>
              ) : (
                <>
                  <DataWindowSlider 
                    aggregation={topicsIntensityStore.aggregation}
                    actualDataLength={getActualDataLength(processedChartData, topicsIntensityStore.aggregation)}
                  />
                  <ChartsGroup
                    loading={topicsIntensityStore.loadingChart}
                    chartData={processedChartData}
                    dataWindow={dataWindow}
                    markerColors={markerColors}
                    getMarkerLabelFunction={getMarkerLabel}
                    getTableDataByDate={(date) => 
                      topicsIntensityStore.fetchTableDataByDate(date, globalFilters)
                    }                    
                  />
                </>
              )}
            </div>

            {/* Таблица */}
            <div className="table-scroll-container">
              {topicsIntensityStore.loadingTable ? (
                <p className="loading">Загрузка...</p>
              ) : topicsIntensityStore.errorTable ? (
                <p className="error">{topicsIntensityStore.errorTable}</p>
              ) : topicsIntensityStore.tableData.length === 0 ? (
                <p className="no-data">Нет данных для отображения</p>
              ) : (
                <div className="table-wrapper">
                  <table className="data-table">
                    <thead>
                      {table.getHeaderGroups().map(headerGroup => (
                        <tr key={headerGroup.id}>
                          {headerGroup.headers.map(header => (
                            <th
                              key={header.id}
                              onClick={header.column.getToggleSortingHandler()}
                              className={header.column.getIsSorted() ? 'sorted' : ''}
                            >
                              {header.isPlaceholder
                                ? null
                                : flexRender(header.column.columnDef.header, header.getContext())}
                              {header.column.getIsSorted() === 'asc' ? ' 🔼' : header.column.getIsSorted() === 'desc' ? ' 🔽' : ''}
                            </th>
                          ))}
                        </tr>
                      ))}
                    </thead>
                    <tbody>
                      {table.getRowModel().rows.map(row => (
                        <tr key={row.id}>
                          {row.getVisibleCells().map(cell => (
                            <td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>
                          ))}
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Пагинация и информация */}
            {!topicsIntensityStore.loadingTable &&
              !topicsIntensityStore.errorTable &&
              topicsIntensityStore.tableData.length > 0 && (
                <div
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginTop: 24,
                  }}
                >
                  <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <span
                        style={{
                          fontWeight: 600,
                          color: '#1976d2',
                          fontSize: 16,
                        }}
                      >
                        Пользовательская классификация
                      </span>
                      <span
                        style={{
                          fontWeight: 500,
                          color: '#6b7280',
                          fontSize: 14,
                        }}
                      >
                        Показано{' '}
                        {topicsIntensityStore.totalRows > 0
                          ? table.getState().pagination.pageIndex *
                              table.getState().pagination.pageSize +
                            1
                          : 0}
                        –
                        {Math.min(
                          (table.getState().pagination.pageIndex + 1) *
                            table.getState().pagination.pageSize,
                          topicsIntensityStore.totalRows
                        )}{' '}
                        из {topicsIntensityStore.totalRows} записей
                      </span>
                    </div>
                    <select
                      value={table.getState().pagination.pageSize}
                      onChange={(e) => table.setPageSize(Number(e.target.value))}
                      style={{
                        padding: '8px 12px',
                        borderRadius: 8,
                        border: '1px solid #4a90e2',
                        background: '#fff',
                        fontSize: 14,
                        color: '#374151',
                        fontWeight: 500,
                        cursor: 'pointer',
                        transition: 'all 0.2s ease',
                        outline: 'none',
                      }}
                      onFocus={(e) => (e.target.style.borderColor = '#357abd')}
                      onBlur={(e) => (e.target.style.borderColor = '#4a90e2')}
                    >
                      {[10, 20, 30, 50].map((size) => (
                        <option key={size} value={size}>
                          Показать {size}
                        </option>
                      ))}
                    </select>
                  </div>
                  <Pagination
                    currentPage={table.getState().pagination.pageIndex + 1}
                    totalPages={table.getPageCount()}
                    onPageChange={(newPage) => table.setPageIndex(newPage - 1)}
                    maxVisiblePages={5}
                  />
                </div>
              )}
          </>
        )}
      </div>
    </div>
  );
});

export default TopicsIntensityAnalysis;